/**
 * Request an idle callback or fallback to setTimeout.
 */
export const requestIdleCallback =
  typeof window.requestIdleCallback === 'function'
    ? window.requestIdleCallback.bind(window)
    : function (callback) {
        return window.setTimeout(() => {
          callback({
            didTimeout: false,
            timeRemaining: () => 0,
          });
        }, 1);
      };

/**
 * Cancel an idle callback or fallback timeout.
 */
export const cancelIdleCallback =
  typeof window.cancelIdleCallback === 'function'
    ? window.cancelIdleCallback.bind(window)
    : function (id) {
        window.clearTimeout(id);
      };

/**
 * Returns a promise that resolves after yielding to the main thread.
 */
export const yieldToMainThread = () => {
  if (typeof window.scheduler !== 'undefined' && typeof window.scheduler.yield === 'function') {
    return window.scheduler.yield();
  }

  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      setTimeout(resolve, 0);
    });
  });
};

export function isLowPowerDevice() {
  return Number(navigator.hardwareConcurrency || 4) <= 2 || Number(navigator.deviceMemory || 4) <= 2;
}

export function supportsViewTransitions() {
  return typeof document.startViewTransition === 'function';
}

export const viewTransition = {
  current: undefined,
};

const viewTransitionTypes = {
  'product-grid': async () => {
    const grid = document.querySelector('.product-grid');
    const productCards = [...document.querySelectorAll('.product-grid .product-grid__item')];

    if (!grid || !productCards.length) return;

    await new Promise((resolve) =>
      requestIdleCallback(() => {
        const cardsToAnimate = getCardsToAnimate(grid, productCards);

        productCards.forEach((card, index) => {
          if (!(card instanceof HTMLElement)) return;

          if (index < cardsToAnimate) {
            card.style.setProperty('view-transition-name', `product-card-${card.dataset.productId}`);
          } else {
            card.style.setProperty('content-visibility', 'hidden');
          }
        });

        resolve(null);
      })
    );

    return () =>
      productCards.forEach((card) => {
        if (!(card instanceof HTMLElement)) return;
        card.style.removeProperty('view-transition-name');
        card.style.removeProperty('content-visibility');
      });
  },
};

export function startViewTransition(callback, types) {
  if (!supportsViewTransitions() || isLowPowerDevice() || prefersReducedMotion()) {
    callback();
    return Promise.resolve();
  }

  return new Promise(async (resolve) => {
    let cleanupFunctions = [];

    if (types) {
      for (const type of types) {
        if (viewTransitionTypes[type]) {
          const cleanupFunction = await viewTransitionTypes[type]();
          if (cleanupFunction) cleanupFunctions.push(cleanupFunction);
        }
      }
    }

    const transition = document.startViewTransition(callback);

    if (!viewTransition.current) {
      viewTransition.current = transition.finished;
    }

    if (types && transition.types) {
      types.forEach((type) => transition.types.add(type));
    }

    transition.finished
      .catch((error) => console.error('View transition failed:', error))
      .finally(() => {
        viewTransition.current = undefined;
        cleanupFunctions.forEach((cleanupFunction) => cleanupFunction());
        resolve();
      });
  });
}

export function fetchConfig(type = 'json', config = {}) {
  const headers = { 'Content-Type': 'application/json', Accept: `application/${type}`, ...config.headers };

  if (type === 'javascript') {
    headers['X-Requested-With'] = 'XMLHttpRequest';
    delete headers['Content-Type'];
  }

  return {
    method: 'POST',
    headers,
    body: config.body,
  };
}

export function debounce(fn, wait) {
  let timeout;

  function debounced(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  }

  debounced.cancel = () => {
    clearTimeout(timeout);
  };

  return debounced;
}

export function throttle(fn, delay) {
  let lastCall = 0;

  function throttled(...args) {
    const now = performance.now();

    if (now - lastCall >= delay) {
      lastCall = now;
      fn.apply(this, args);
    }
  }

  throttled.cancel = () => {
    lastCall = performance.now();
  };

  return throttled;
}

const reducedMotion =
  typeof window.matchMedia === 'function'
    ? matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };

export function prefersReducedMotion() {
  return reducedMotion.matches;
}

export function normalizeString(str) {
  return String(str || '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

export function onDocumentLoaded(callback) {
  if (document.readyState === 'complete') {
    callback();
  } else {
    window.addEventListener('load', callback, { once: true });
  }
}

export function onDocumentReady(callback) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback, { once: true });
  } else {
    callback();
  }
}

export function removeWillChangeOnAnimationEnd(event) {
  const target = event.target;

  if (target && target instanceof HTMLElement) {
    target.style.setProperty('will-change', 'unset');
    target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
  }
}

export function onAnimationEnd(elements, callback, options = { subtree: true }) {
  const elementList = Array.isArray(elements) ? elements : [elements];

  const animations = elementList.flatMap((element) => {
    if (!element || typeof element.getAnimations !== 'function') return [];
    return element.getAnimations(options);
  });

  const animationPromises = animations.reduce((acc, animation) => {
    if (typeof DocumentTimeline === 'undefined' || animation.timeline instanceof DocumentTimeline) {
      acc.push(animation.finished.catch(() => null));
    }

    return acc;
  }, []);

  return Promise.allSettled(animationPromises).then(() => {
    if (typeof callback === 'function') callback();
  });
}

export function isClickedOutside(event, element) {
  if (!(element instanceof Element)) return true;

  if (event.target instanceof HTMLDialogElement || !(event.target instanceof Element)) {
    return !isPointWithinElement(event.clientX, event.clientY, element);
  }

  return !element.contains(event.target);
}

export function isPointWithinElement(x, y, element) {
  if (!(element instanceof Element)) return false;

  const { left, right, top, bottom } = element.getBoundingClientRect();

  return x >= left && x <= right && y >= top && y <= bottom;
}

export const mediaQueryLarge =
  typeof window.matchMedia === 'function' ? matchMedia('(min-width: 750px)') : { matches: false };

export function isMobileBreakpoint() {
  return !mediaQueryLarge.matches;
}

export function isDesktopBreakpoint() {
  return mediaQueryLarge.matches;
}

export function isTouchDevice() {
  return 'ontouchstart' in window && navigator.maxTouchPoints > 0;
}

export function clamp(value, min, max) {
  return Math.max(min, Math.min(value, max));
}

export function center(element, axis) {
  if (!(element instanceof Element)) {
    const point = { x: 0, y: 0 };
    return axis ? point[axis] : point;
  }

  const { left, width, top, height } = element.getBoundingClientRect();

  const point = {
    x: left + width / 2,
    y: top + height / 2,
  };

  if (axis) return point[axis];

  return point;
}

export function start(element, axis) {
  if (!(element instanceof Element)) {
    const point = { x: 0, y: 0 };
    return axis ? point[axis] : point;
  }

  const { left, top } = element.getBoundingClientRect();
  const point = { x: left, y: top };

  if (axis) return point[axis];

  return point;
}

export function closest(values, target) {
  if (!Array.isArray(values) || !values.length) return target;

  return values.reduce(function (prev, curr) {
    return Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev;
  });
}

export function preventDefault(event) {
  event.preventDefault();
}

export function getVisibleElements(root, elements, ratio = 1, axis) {
  if (!(root instanceof Element) || !elements?.length) return [];

  const rootRect = root.getBoundingClientRect();

  return elements.filter((element) => {
    if (!(element instanceof Element)) return false;

    const { width, height, top, right, left, bottom } = element.getBoundingClientRect();

    if (ratio < 1) {
      const intersectionLeft = Math.max(rootRect.left, left);
      const intersectionRight = Math.min(rootRect.right, right);
      const intersectionWidth = Math.max(0, intersectionRight - intersectionLeft);

      if (axis === 'x') {
        return width > 0 && intersectionWidth / width >= ratio;
      }

      const intersectionTop = Math.max(rootRect.top, top);
      const intersectionBottom = Math.min(rootRect.bottom, bottom);
      const intersectionHeight = Math.max(0, intersectionBottom - intersectionTop);

      if (axis === 'y') {
        return height > 0 && intersectionHeight / height >= ratio;
      }

      const intersectionArea = intersectionWidth * intersectionHeight;
      const elementArea = width * height;

      return elementArea > 0 && intersectionArea / elementArea >= ratio;
    }

    const isWithinX = left >= rootRect.left && right <= rootRect.right;

    if (axis === 'x') {
      return isWithinX;
    }

    const isWithinY = top >= rootRect.top && bottom <= rootRect.bottom;

    if (axis === 'y') {
      return isWithinY;
    }

    return isWithinX && isWithinY;
  });
}

export function getIOSVersion() {
  const { userAgent } = navigator;
  const isIOS = /(iPhone|iPad)/i.test(userAgent);

  if (!isIOS) return null;

  const version = userAgent.match(/OS ([\d_]+)/)?.[1];
  const [major, minor] = version?.split('_') || [];

  if (!version || !major) return null;

  return {
    fullString: version.replace('_', '.'),
    major: parseInt(major, 10),
    minor: minor ? parseInt(minor, 10) : 0,
  };
}

function getCardsToAnimate(grid, cards) {
  if (!grid || !cards || cards.length === 0) return 0;

  const itemSample = cards[0];

  if (!itemSample) return 0;

  const gridRect = grid.getBoundingClientRect();

  const visibleArea = {
    top: Math.max(0, gridRect.top),
    bottom: Math.min(window.innerHeight, gridRect.bottom),
  };

  const visibleHeight = Math.round(visibleArea.bottom - visibleArea.top);

  if (visibleHeight <= 0) return 0;

  const cardSample = itemSample.querySelector('product-card');
  const gridStyle = getComputedStyle(grid);

  const galleryAspectRatio = cardSample?.refs?.cardGallery?.style.getPropertyValue('--gallery-aspect-ratio') || '';
  let aspectRatio = parseFloat(galleryAspectRatio) || 0.5;

  if (galleryAspectRatio?.includes('/')) {
    const [width = '1', height = '2'] = galleryAspectRatio.split('/');
    aspectRatio = parseInt(width, 10) / parseInt(height, 10);
  }

  const cardGap = parseInt(cardSample?.refs?.productCardLink?.style.getPropertyValue('--product-card-gap') || '') || 12;
  const gridGap = parseInt(gridStyle.getPropertyValue('--product-grid-gap')) || 12;
  const detailsSize = ((parseInt(gridStyle.fontSize) || 16) + 2) * 2;
  const isMobile = window.innerWidth < 750;
  const cardWidth = isMobile ? Math.round((gridRect.width - gridGap) / 2) : 100;
  const cardHeight = Math.round(cardWidth / aspectRatio) + cardGap + detailsSize;
  const columnsInGrid = isMobile ? 2 : Math.floor((gridRect.width + gridGap) / (cardWidth + gridGap));
  const rowsInGrid = Math.ceil((visibleHeight - gridGap) / (cardHeight + gridGap));

  return columnsInGrid * rowsInGrid;
}

export function preloadImage(src) {
  if (!src) return;

  const image = new Image();
  image.src = src;
}

export class TextComponent extends HTMLElement {
  shimmer() {
    this.setAttribute('shimmer', '');
  }
}

if (!customElements.get('text-component')) {
  customElements.define('text-component', TextComponent);
}

export function resetShimmer(container = document.body) {
  if (!(container instanceof Element)) return;

  const shimmer = container.querySelectorAll('[shimmer]');
  shimmer.forEach((item) => item.removeAttribute('shimmer'));
}

export function changeMetaThemeColor(color) {
  const metaThemeColor = document.head.querySelector('meta[name="theme-color"]');

  if (metaThemeColor && color) {
    metaThemeColor.setAttribute('content', color);
  }
}

export function getViewParameterValue() {
  return new URLSearchParams(window.location.search).get('view');
}

export function parseIntOrDefault(value, defaultValue) {
  if (value === null || value === undefined || value === '') {
    return defaultValue;
  }

  const parsed = parseInt(value.toString());

  return isNaN(parsed) ? defaultValue : parsed;
}

class Scheduler {
  #queue = new Set();
  #scheduled = false;

  schedule = async (task) => {
    if (typeof task !== 'function') return;

    this.#queue.add(task);

    if (!this.#scheduled) {
      this.#scheduled = true;

      if (viewTransition.current) await viewTransition.current;

      requestAnimationFrame(this.flush);
    }
  };

  flush = () => {
    for (const task of this.#queue) {
      setTimeout(task, 0);
    }

    this.#queue.clear();
    this.#scheduled = false;
  };
}

export const scheduler = new Scheduler();

export function oncePerEditorSession(element, sessionKeyName, callback) {
  const isInThemeEditor = window.Shopify?.designMode;

  let shopifyEditorSectionId = null;
  let shopifyEditorBlockId = null;

  try {
    shopifyEditorSectionId = JSON.parse(element.dataset.shopifyEditorSection || '{}').id;
    shopifyEditorBlockId = JSON.parse(element.dataset.shopifyEditorBlock || '{}').id;
  } catch {
    shopifyEditorSectionId = null;
    shopifyEditorBlockId = null;
  }

  const editorId = shopifyEditorSectionId || shopifyEditorBlockId;
  const uniqueSessionKey = `${sessionKeyName}-${editorId}`;

  if (isInThemeEditor && sessionStorage.getItem(uniqueSessionKey)) return;

  callback();

  if (isInThemeEditor) sessionStorage.setItem(uniqueSessionKey, 'true');
}

export class ResizeNotifier extends ResizeObserver {
  #initialized = false;

  constructor(callback) {
    super((entries) => {
      if (this.#initialized) return callback(entries, this);
      this.#initialized = true;
    });
  }

  disconnect() {
    this.#initialized = false;
    super.disconnect();
  }
}

export function setHeaderMenuStyle() {
  const headerComponent = document.querySelector('#header-component');

  if (headerComponent) {
    window.requestAnimationFrame(() => {
      const overflowList = headerComponent?.querySelector('overflow-list');
      const hasReachedMinimum = overflowList && overflowList.hasAttribute('minimum-reached');
      headerComponent.dataset.menuStyle = isTouchDevice() || hasReachedMinimum ? 'drawer' : 'menu';
    });
  }
}

export function calculateHeaderGroupHeight(
  header = document.querySelector('#header-component'),
  headerGroup = document.querySelector('#header-group')
) {
  if (!headerGroup) return 0;

  let totalHeight = 0;
  const children = headerGroup.children;

  for (let i = 0; i < children.length; i++) {
    const element = children[i];

    if (element === header || !(element instanceof HTMLElement)) continue;

    totalHeight += element.offsetHeight;
  }

  if (header instanceof HTMLElement && header.hasAttribute('transparent') && header.parentElement?.nextElementSibling) {
    return totalHeight + header.offsetHeight;
  }

  return totalHeight;
}

function updateTransparentHeaderOffset() {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');
  const hasHeaderSection = headerGroup?.querySelector('.header-section');

  if (!hasHeaderSection || !header?.hasAttribute('transparent')) {
    document.body.style.setProperty('--transparent-header-offset-boolean', '0');
    return;
  }

  const hasImmediateSection = hasHeaderSection.nextElementSibling?.classList.contains('shopify-section');
  const shouldApplyOffset = !hasImmediateSection ? '1' : '0';

  document.body.style.setProperty('--transparent-header-offset-boolean', shouldApplyOffset);
}

function updateHeaderHeights() {
  const header = document.querySelector('header-component');

  if (!(header instanceof HTMLElement)) return;

  const headerHeight = header.offsetHeight;
  const headerGroupHeight = calculateHeaderGroupHeight(header);
  const headerMenuRow = header.querySelector('.header__row:has(.header-menu)');

  document.body.style.setProperty('--header-height', `${headerHeight}px`);
  document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);

  if (headerMenuRow) {
    window.requestAnimationFrame(function () {
      header.style.setProperty('--menu-row-height', `${headerMenuRow.offsetHeight}px`);
    });
  }
}

export function updateAllHeaderCustomProperties() {
  updateHeaderHeights();
  updateTransparentHeaderOffset();
  setHeaderMenuStyle();
}

if (typeof Theme !== 'undefined') {
  Theme.utilities = {
    ...Theme.utilities,
    scheduler: scheduler,
  };
}